import { AlertTitle, List } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import {
  GetConfigVarsDocument,
  GetConfigVarsQuery,
} from '../../../../generated/graphql'
import { Link } from '@sitoo/mui-components'
import { HashLink } from '../../../../utils/hash-link'
import { useMemo } from 'react'

type FeatureKey = Exclude<keyof GetConfigVarsQuery['configVars'], '__typename'>

const featureKeys = [
  'taxFreeExport',
  'taxFreeDutyFree',
  'webApps',
  'crmv2Channels',
  'posUiConfig',
] satisfies FeatureKey[]

type PosFeature = (typeof featureKeys)[number]

type FeatureMap = Record<
  PosFeature,
  {
    link: string | undefined
    text: string
    prefix: string
  }
>

const PosSettingsLink = ({ feature }: { feature: PosFeature }) => {
  const { data: configVarsData } = useQuery(GetConfigVarsDocument)
  const { t } = useTranslation(['settings'])

  const features = useMemo<FeatureMap>(() => {
    return {
      taxFreeExport: {
        link: undefined,
        text: t('settings:general.pos_settings.tax_free_export'),
        prefix: t('settings:general.pos_settings.apps'),
      },
      taxFreeDutyFree: {
        link: undefined,
        text: t('settings:general.pos_settings.tax_free_duty_free'),
        prefix: t('settings:general.pos_settings.apps'),
      },
      posUiConfig: {
        link: '#pos-ui',
        text: t('settings:general.pos_settings.pos_ui_config'),
        prefix: t('settings:general.pos_settings.general_settings'),
      },
      webApps: {
        link: undefined,
        text: t('settings:general.pos_settings.web_apps'),
        prefix: t('settings:general.pos_settings.apps'),
      },
      crmv2Channels: {
        link: undefined,
        text: t('settings:general.pos_settings.crm'),
        prefix: t('settings:general.pos_settings.apps'),
      },
    }
  }, [t])

  if (!configVarsData?.configVars[feature]) return null

  const { link, text, prefix } = features[feature]

  let linkElement = (
    <span>{`${text} (${t('settings:general.pos_settings.not_yet_implemented')})`}</span>
  )

  if (link) {
    linkElement = (
      <Link to={link} component={HashLink}>
        {text}
      </Link>
    )
  }

  return (
    <li>
      {prefix} - {linkElement}
    </li>
  )
}

export const useShouldShowPosSettingsInfo = () => {
  const { data: configVarsData } = useQuery(GetConfigVarsDocument)

  const hasEnabledFeatures = featureKeys.some(
    (feature) => configVarsData?.configVars?.[feature],
  )

  return hasEnabledFeatures
}

export const PosSettingsInfo = () => {
  const { t } = useTranslation(['settings'])
  const hasEnabledFeatures = useShouldShowPosSettingsInfo()

  if (!hasEnabledFeatures) return null

  return (
    <>
      <AlertTitle>
        {t('settings:general.pos_settings.information_title')}
      </AlertTitle>

      {t('settings:general.pos_settings.information_description')}

      <List
        sx={{
          listStyleType: 'disc',
          mt: 2,
          fontWeight: 'normal',
          li: {
            ml: 1.5,
          },
        }}
      >
        {featureKeys.map((feature) => (
          <PosSettingsLink key={feature} feature={feature} />
        ))}
      </List>
    </>
  )
}
